import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';

import { Badge, Button, Card, MonoLabel } from '@/design-system';
import type { SubscriptionOverview } from '@/entities/credit';
import { formatMoney } from '@/entities/invoice';
import type { Plan, PriceList, TenantTrial } from '@/entities/tenant';
import { formatCurrencyFromCents, formatDayLong } from '@/shared/lib';

import { billingCopy } from '../copy';
import { withVat } from '../vat';

/** Het anker waar "Kies een abonnement" naartoe wijst (`paths.choosePlan`). */
export const PLAN_CHOICE_ANCHOR = 'abonnement';

export interface SubscriptionCardProps {
    overview: SubscriptionOverview;
    /** De prijzenlijst (`GET /public/pricing`), voor de abonnementen om uit te kiezen. */
    priceList: PriceList | null;
    /** De proef van het bedrijf, of `null` als het al betaalt. */
    trial: TenantTrial | null;
    /** Er loopt een actie (kiezen, wisselen, opzeggen…): de knoppen wachten. */
    busy: boolean;
    error: string | null;
    /** Een korte bevestiging na een wissel, opzegging of terugdraaiing. */
    notice: string | null;
    onChoose: (planCode: string) => void;
    onSwitch: (planCode: string) => void;
    onCancel: () => void;
    onResume: () => void;
    /** Mislukte incasso of openstaande maand: opnieuw (of nu) betalen. */
    onPayAgain: () => void;
    /** Betaallink: de openstaande maand nu al betalen, via dezelfde betaalroute als de link in de mail. */
    onPayNow: () => void;
}

/**
 * Het abonnement bij Smartkeur, op Facturatie (`#abonnement`).
 *
 * - **Geen abonnement** (in de proef, of nadat het afliep): de abonnementen van
 *   de prijzenlijst, de keuze van de website vooraan, elk met een knop die de
 *   eerste maand laat betalen.
 * - **Actief**: het abonnement, de keuringen van deze maand (inbegrepen en hoeveel
 *   er over zijn), de volgende afschrijving en de datum, wisselen (per de
 *   volgende maand) en opzeggen.
 * - **Opgezegd**: tot wanneer het loopt, en de opzegging terugdraaien.
 * - **Mislukte incasso**: wat dat betekent en opnieuw betalen.
 * - **Betaallink** (geen machtiging mogelijk): dat je per maand met een link betaalt
 *   en wanneer de volgende komt; staat de maand open, dan een knop "Nu betalen".
 *   Niet betaald na een week: dezelfde banner als bij een mislukte incasso, met de
 *   knop naar de betaalroute van de link.
 *
 * "Kies een abonnement" en "Groter abonnement kiezen" op het dashboard en bij een 402 komen hier
 * uit, en het blok scrolt zich dan in beeld.
 */
export function SubscriptionCard(props: SubscriptionCardProps) {
    const { hash } = useLocation();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (hash === `#${PLAN_CHOICE_ANCHOR}`) {
            ref.current?.scrollIntoView?.({ block: 'start' });
        }
    }, [hash]);

    const { overview } = props;

    return (
        <Card
            ref={ref}
            id={PLAN_CHOICE_ANCHOR}
            padding="md"
            as="section"
            aria-labelledby="plan-choice-title"
            variant={overview.state === 'past_due' ? 'warn' : 'default'}
        >
            <h2
                id="plan-choice-title"
                className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {billingCopy.planTitle}
            </h2>

            {overview.state === 'none' || overview.subscription === null ? (
                <ChoosePlan {...props} />
            ) : (
                <CurrentPlan {...props} />
            )}

            {props.notice ? (
                <p
                    role="status"
                    className="m-0 mt-3 text-[13px] leading-[1.5] text-[var(--accent-dark)]"
                >
                    {props.notice}
                </p>
            ) : null}
            {props.error ? (
                <p role="alert" className="m-0 mt-3 text-[13px] leading-[1.5] text-[var(--danger)]">
                    {props.error}
                </p>
            ) : null}
        </Card>
    );
}

function ChoosePlan({ overview, priceList, trial, busy, onChoose }: SubscriptionCardProps) {
    const plans = orderedPlans(priceList?.plans ?? [], overview.planPreference);

    return (
        <>
            <Paragraph strong>
                {trial ? billingCopy.planTrial(trial.inspectionsRemaining) : billingCopy.planNone}
            </Paragraph>
            <Paragraph muted>{billingCopy.planIntro}</Paragraph>

            <ul className="m-0 mt-[14px] grid list-none gap-[10px] p-0 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
                {plans.map((plan) => (
                    <li
                        key={plan.code}
                        className="flex flex-col rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] px-[14px] py-3"
                    >
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="font-[family-name:var(--font-display)] text-[15px] font-semibold text-[var(--text-primary)]">
                                {plan.name}
                            </span>
                            {plan.featured ? (
                                <Badge tone="accent">{billingCopy.planFeatured}</Badge>
                            ) : null}
                            {overview.planPreference === plan.code ? (
                                <Badge tone="neutral">{billingCopy.planPreferred}</Badge>
                            ) : null}
                        </div>
                        <MonoLabel
                            size={11}
                            as="div"
                            variant="value"
                            tone="default"
                            className="mt-2"
                        >
                            {billingCopy.planPerMonth(
                                formatCurrencyFromCents(plan.monthlyPriceCents),
                            )}
                        </MonoLabel>
                        <div className="mt-1 text-[12px] text-[var(--text-tertiary-safe)]">
                            {billingCopy.planPerMonthInclVat(
                                formatCurrencyFromCents(
                                    withVat(plan.monthlyPriceCents, overview.purchase.vatPercent)
                                        .totalCents,
                                    { cents: true },
                                ),
                            )}
                        </div>
                        <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                            {billingCopy.planInspections(plan.includedInspections)}
                        </div>
                        {plan.description ? (
                            <p className="m-0 mt-2 text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                                {plan.description}
                            </p>
                        ) : null}
                        <Button
                            variant={overview.planPreference === plan.code ? 'primary' : 'outline'}
                            size="md"
                            fullWidth
                            className="mt-3"
                            disabled={busy}
                            onClick={() => onChoose(plan.code)}
                        >
                            {billingCopy.planChoose(plan.name)}
                        </Button>
                    </li>
                ))}
            </ul>

            <Paragraph muted>{billingCopy.planChooseHelp}</Paragraph>
            {priceList ? (
                <Paragraph muted>
                    {billingCopy.planExtras(
                        formatCurrencyFromCents(priceList.extraInspectionPriceCents),
                        formatCurrencyFromCents(priceList.singleReport.priceCents),
                        priceList.singleReport.name,
                    )}
                </Paragraph>
            ) : null}
        </>
    );
}

function CurrentPlan({
    overview,
    priceList,
    busy,
    onSwitch,
    onCancel,
    onResume,
    onPayAgain,
    onPayNow,
}: SubscriptionCardProps) {
    const [confirmingCancel, setConfirmingCancel] = useState(false);
    const subscription = overview.subscription;

    if (subscription === null) {
        return null;
    }

    const paysByLink = subscription.billingMethod === 'link';
    const paymentLink = overview.paymentLink;
    const monthAmount = formatCurrencyFromCents(
        withVat(
            (subscription.nextPlan ?? subscription.plan).monthlyPriceCents,
            overview.purchase.vatPercent,
        ).totalCents,
        { cents: true },
    );

    const periodEnd = subscription.currentPeriodEnd
        ? formatDayLong(subscription.currentPeriodEnd)
        : null;
    const nextPlan = subscription.nextPlan;
    const others = (priceList?.plans ?? []).filter(
        (plan) => plan.code !== (nextPlan ?? subscription.plan).code,
    );

    return (
        <>
            <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="font-[family-name:var(--font-display)] text-[17px] font-semibold text-[var(--text-primary)]">
                    {billingCopy.planCurrent(subscription.plan.name)}
                </span>
                <Badge tone={overview.state === 'active' ? 'accent' : 'warn'}>
                    {overview.stateLabel}
                </Badge>
            </div>

            {overview.state === 'past_due' ? (
                <div className="mt-3">
                    <p className="m-0 text-[14px] leading-[1.4] font-semibold text-[var(--warn-ink)]">
                        {paysByLink
                            ? billingCopy.planPastDueTitleLink
                            : billingCopy.planPastDueTitle}
                    </p>
                    <Paragraph warn>
                        {paysByLink
                            ? billingCopy.planPastDueLink(monthAmount)
                            : billingCopy.planPastDue(monthAmount)}
                    </Paragraph>
                    <Button
                        variant="warn"
                        size="md"
                        className="mt-3"
                        disabled={busy}
                        onClick={onPayAgain}
                    >
                        {paysByLink ? billingCopy.planPayNow : billingCopy.planPayAgain}
                    </Button>
                </div>
            ) : null}

            <dl className="m-0 mt-3 border-t border-[var(--border-subtle-2)] pt-2">
                <Row label={billingCopy.planThisMonth}>
                    {billingCopy.planIncludedLeft(
                        overview.included.left,
                        overview.included.perMonth,
                    )}
                </Row>
                {overview.nextCharge ? (
                    <Row
                        label={
                            paysByLink ? billingCopy.planNextPayment : billingCopy.planNextCharge
                        }
                    >
                        {billingCopy.planNextChargeValue(
                            formatMoney(overview.nextCharge.amount),
                            overview.nextCharge.date
                                ? formatDayLong(overview.nextCharge.date)
                                : null,
                        )}
                    </Row>
                ) : null}
            </dl>

            {overview.included.expiresAt && overview.state !== 'past_due' ? (
                <Paragraph muted>
                    {billingCopy.planIncludedExpire(formatDayLong(overview.included.expiresAt))}
                </Paragraph>
            ) : null}

            {paysByLink && overview.state === 'active' ? (
                <div className="mt-2">
                    {paymentLink?.due ? (
                        <>
                            <Paragraph>{billingCopy.planLinkInfo}</Paragraph>
                            <Paragraph strong>{billingCopy.planLinkDue(monthAmount)}</Paragraph>
                            <Button
                                variant="primary"
                                size="md"
                                className="mt-3"
                                disabled={busy}
                                onClick={onPayNow}
                            >
                                {billingCopy.planPayNow}
                            </Button>
                        </>
                    ) : (
                        <Paragraph>
                            {paymentLink
                                ? billingCopy.planLinkNext(formatDayLong(paymentLink.sendsOn))
                                : billingCopy.planLinkInfo}
                        </Paragraph>
                    )}
                </div>
            ) : null}

            {nextPlan && periodEnd ? (
                <Paragraph>{billingCopy.planNextPlan(nextPlan.name, periodEnd)}</Paragraph>
            ) : null}

            {overview.state === 'cancelling' ? (
                <>
                    <Paragraph>
                        {billingCopy.planCancelling(
                            subscription.endsAt ? formatDayLong(subscription.endsAt) : '—',
                        )}
                    </Paragraph>
                    <Button
                        variant="primary"
                        size="md"
                        className="mt-3"
                        disabled={busy}
                        onClick={onResume}
                    >
                        {billingCopy.planResume}
                    </Button>
                </>
            ) : null}

            {overview.state === 'active' ? (
                <div className="mt-3 border-t border-[var(--border-subtle-2)] pt-3">
                    <h3 className="m-0 text-[13px] leading-[1.4] font-semibold text-[var(--text-primary)]">
                        {billingCopy.planSwitchTitle}
                    </h3>
                    {periodEnd ? (
                        <Paragraph muted>{billingCopy.planSwitchHelp(periodEnd)}</Paragraph>
                    ) : null}
                    <div className="mt-2 flex flex-wrap gap-2">
                        {nextPlan ? (
                            <Button
                                variant="outline"
                                size="md"
                                disabled={busy}
                                onClick={() => onSwitch(subscription.plan.code)}
                            >
                                {billingCopy.planStay(subscription.plan.name)}
                            </Button>
                        ) : null}
                        {others
                            .filter((plan) => plan.code !== subscription.plan.code)
                            .map((plan) => (
                                <Button
                                    key={plan.code}
                                    variant="outline"
                                    size="md"
                                    disabled={busy}
                                    onClick={() => onSwitch(plan.code)}
                                >
                                    {billingCopy.planSwitchTo(plan.name)}
                                </Button>
                            ))}
                    </div>

                    {confirmingCancel ? (
                        <Card
                            variant="warn"
                            padding="md"
                            role="group"
                            aria-label={billingCopy.planCancelConfirmLabel}
                            className="mt-3"
                        >
                            <Paragraph warn>
                                {billingCopy.planCancelConfirm(periodEnd ?? '—')}
                            </Paragraph>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <Button
                                    variant="warn"
                                    size="md"
                                    disabled={busy}
                                    onClick={() => {
                                        setConfirmingCancel(false);
                                        onCancel();
                                    }}
                                >
                                    {billingCopy.planCancelYes}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="md"
                                    onClick={() => setConfirmingCancel(false)}
                                >
                                    {billingCopy.cancel}
                                </Button>
                            </div>
                        </Card>
                    ) : (
                        <Button
                            variant="outline"
                            size="md"
                            className="mt-3"
                            disabled={busy}
                            onClick={() => setConfirmingCancel(true)}
                        >
                            {billingCopy.planCancel}
                        </Button>
                    )}
                </div>
            ) : null}
        </>
    );
}

/** De abonnementen in de volgorde van de prijzenlijst, met de keuze van de website vooraan. */
function orderedPlans(plans: readonly Plan[], preference: string | null): Plan[] {
    const preferred = plans.filter((plan) => plan.code === preference);

    return [...preferred, ...plans.filter((plan) => plan.code !== preference)];
}

function Row({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-[5px]">
            <dt className="text-[13px] leading-[1.4] text-[var(--text-secondary)]">{label}</dt>
            <dd className="m-0 text-right font-[family-name:var(--font-mono)] text-[12px] text-[var(--text-primary)]">
                {children}
            </dd>
        </div>
    );
}

function Paragraph({
    children,
    strong = false,
    muted = false,
    warn = false,
}: {
    children: ReactNode;
    strong?: boolean;
    muted?: boolean;
    warn?: boolean;
}) {
    const tone = warn
        ? 'text-[var(--warn-ink)]'
        : muted
          ? 'text-[var(--text-tertiary-safe)]'
          : 'text-[var(--text-primary)]';

    return (
        <p
            className={`m-0 mt-2 ${muted ? 'text-[12px]' : 'text-[14px]'} leading-[1.5] ${strong ? 'font-medium' : ''} ${tone}`}
        >
            {children}
        </p>
    );
}
